// Save All, Dry run, Discard All (spec/dashboard.md section 4.3–4.5): the commit message, the reason of player writes, the
// toast with its next steps (View commit, Build release) and the Changes panel on a failure.
import { createElement, useCallback, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { useConfirm } from '../ui/ConfirmDialog';
import { ReasonDialog } from '../ui/ReasonDialog';
import { useToasts, type ToastAction } from '../ui/Toast';
import { queryCache } from '../workspace/cache';
import { saveAll, type PreSaveHook } from '../workspace/save';
import { summarizeDrafts } from '../workspace/summary';
import type { Workspace } from '../workspace/Workspace';
import { useGame } from './GameContext';
import { routes } from './routes';

export interface SaveAllOptions {
  /** called after a real save (not a dry run) that left drafts behind: the shell opens the Changes panel (section 4.3 step 4) */
  onFailure?: () => void;
}

const TOAST_MESSAGE_MAX = 60;

/**
 * The shell's Save All / Dry run / Discard All (spec section 4.3–4.5), bound to the current branch workspace. The commit
 * message is the explicit one, else what the user typed in the Changes panel (`workspace.saveMessage`), else the automatic
 * summary of the drafts (../workspace/summary.ts). When a player key is among the drafts the save first asks for the reason
 * every player write carries (section 11.4) — a separate thing from the commit message, never filled by the summary;
 * `reasonDialog` is the element the shell renders for that.
 */
export function useSaveAll(workspace: Workspace | null, hooks: PreSaveHook[] = [], options: SaveAllOptions = {}) {
  const { client } = useAuth();
  const { org, game, environments, can } = useGame();
  const navigate = useNavigate();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [reasonRequest, setReasonRequest] = useState<{ resolve: (reason: string | null) => void } | null>(null);
  const askReason = useCallback(() => new Promise<string | null>((resolve) => setReasonRequest({ resolve })), []);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // the success toast's next steps (section 4.3): the commit just made, and a release of the branch for whoever may build one;
  // an environment workspace (`env:<slug>`, GameLayout) commits nothing to a branch, so it offers neither
  const deployEnv = environments.find((e) => can('deploy', e.slug));
  const nextSteps = useCallback(
    (commit: string | null | undefined): ToastAction[] | undefined => {
      if (!workspace || !commit || workspace.branch.startsWith('env:')) return undefined;
      const branch = workspace.branch;
      const actions: ToastAction[] = [{ label: 'View commit', onClick: () => navigate(routes.history(org, game.slug, branch, commit)) }];
      if (deployEnv) actions.push({ label: 'Build release', onClick: () => navigate(`${routes.releases(org, game.slug, branch, 'releases')}?build=1`) });
      return actions;
    },
    [workspace, navigate, org, game.slug, deployEnv],
  );

  const run = useCallback(
    async (dryRun: boolean, message?: string) => {
      if (!workspace || saving) return null;
      const explicit = message?.trim() ?? '';
      let reason: string | undefined = explicit || undefined;
      if (!dryRun && !reason && workspace.all.some((d) => d.kind === 'player-key')) {
        reason = (await askReason()) ?? undefined;
        if (!reason) return null; // cancelled: nothing is saved (section 11.4)
      }
      const commitMessage = explicit || workspace.saveMessage.trim() || summarizeDrafts(workspace);
      // only player keys: nothing is committed to the branch, the reason is their message
      const commits = workspace.all.some((d) => d.kind !== 'player-key');
      setSaving(true);
      try {
        const summary = await saveAll(workspace, { client, org, game: game.slug, branch: workspace.branch, hooks, dryRun, message: commitMessage, reason: reason ?? '' });
        queryCache.invalidate(`branch:${game.id}:${workspace.branch}`);
        const failed = summary.failed + summary.blocked;
        const notes = summary.notes?.length ? ` ${summary.notes.join('; ')}.` : '';
        const shown = commitMessage.length > TOAST_MESSAGE_MAX ? `${commitMessage.slice(0, TOAST_MESSAGE_MAX).trimEnd()}…` : commitMessage;
        if (dryRun) toasts.info(`Dry run: ${summary.saved} valid, ${failed} would be refused.`);
        else if (failed === 0) toasts.success(`Saved ${summary.saved} change${summary.saved === 1 ? '' : 's'}${commits && shown ? `: ${shown}` : '.'}${notes}`, { actions: nextSteps(summary.commit) });
        else toasts.error(`${summary.saved} saved, ${failed} not saved — see the Changes panel; nothing staged was lost.${notes}`);
        if (!dryRun && failed === 0) workspace.setSaveMessage('');
        if (!dryRun && failed > 0) optionsRef.current.onFailure?.();
        return summary;
      } finally {
        setSaving(false);
      }
    },
    [workspace, saving, client, org, game, hooks, toasts, askReason, nextSteps],
  );

  const discardAll = useCallback(async () => {
    if (!workspace) return;
    const ok = await confirm({ title: 'Discard all changes?', body: `${workspace.changeCount} staged change${workspace.changeCount === 1 ? '' : 's'} will be thrown away. This cannot be undone.`, confirmLabel: 'Discard all', tone: 'danger' });
    if (ok) workspace.discardAll();
  }, [workspace, confirm]);

  const reasonDialog: ReactNode = reasonRequest
    ? createElement(ReasonDialog, {
        open: true,
        onResult: (reason) => {
          reasonRequest.resolve(reason);
          setReasonRequest(null);
        },
      })
    : null;

  return { saveAll: (message?: string) => run(false, message), dryRun: () => run(true), discardAll, saving, reasonDialog };
}
